import { useMemo, useState } from 'react';
import { slugify, type BrandInput } from '@motorcity/shared';
import { useAuth } from '../auth/AuthProvider';
import { useBrands, useCreateBrand, useDeleteBrand, useReorderBrands } from '../api/hooks';
import { ErrorNote, PageHeader } from '../components/Layout';
import { CardGrid, StatusFilter, type GridCard } from '../components/CardGrid';
import { EmptyState, GridSkeleton } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';
import { getConfig } from '../config';

export function BrandsPage() {
  const { user } = useAuth();
  const brands = useBrands();
  const reorder = useReorderBrands();
  const remove = useDeleteBrand();
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<'all' | 'live' | 'draft'>('all');
  const toast = useToast();

  const all = useMemo<GridCard[]>(() => {
    const list = brands.data?.brands ?? [];
    const counts = brands.data?.counts ?? {};
    return list.map((brand) => {
      const c = counts[brand.brandId];
      return {
        id: brand.brandId,
        title: brand.name.en,
        subtitle: brand.name.ar || undefined,
        facts: c
          ? [
              `${c.models} model${c.models === 1 ? '' : 's'}`,
              `${c.liveModels} live`,
              ...(c.variants ? [`${c.variants} variants`] : []),
            ]
          : undefined,
        status: brand.status,
        href: `/brands/${brand.brandId}`,
        imageUrl: brand.logo ? `${getConfig().contentBaseUrl}/${brand.logo.key}` : undefined,
        fit: 'contain' as const,
      };
    });
  }, [brands.data]);

  const counts = {
    all: all.length,
    live: all.filter((b) => b.status === 'live').length,
    draft: all.filter((b) => b.status === 'draft').length,
  };
  const visible = filter === 'all' ? all : all.filter((b) => b.status === filter);

  if (brands.isLoading) {
    return (
      <div className="page wide">
        <PageHeader title="Brands" />
        <GridSkeleton cards={6} />
      </div>
    );
  }
  if (brands.error) return <ErrorNote error={brands.error} />;

  return (
    <div className="page wide">
      <PageHeader title="Brands">
        {all.length > 0 ? (
          <StatusFilter value={filter} onChange={setFilter} counts={counts} />
        ) : null}
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          New brand
        </button>
      </PageHeader>

      {creating ? <NewBrandForm onDone={() => setCreating(false)} /> : null}

      {all.length === 0 && !creating ? (
        <section className="card">
          <EmptyState
            title="No brands yet"
            body="Create the first brand to start building the catalog."
            action={
              <button type="button" className="btn primary" onClick={() => setCreating(true)}>
                New brand
              </button>
            }
          />
        </section>
      ) : visible.length === 0 ? (
        <section className="card">
          <EmptyState
            title={`Nothing is ${filter}`}
            body={`No brands currently have ${filter} status.`}
            action={
              <button type="button" className="btn" onClick={() => setFilter('all')}>
                Show all
              </button>
            }
          />
        </section>
      ) : (
        <CardGrid
          items={visible}
          canDelete={Boolean(user?.isAdmin)}
          // Reordering a filtered view would silently reshuffle hidden siblings,
          // so the full list is what gets persisted.
          onReorder={(ids) => {
            const order = new Map(ids.map((id, i) => [id, i]));
            reorder.mutate(
              [...all]
                .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
                .map((b) => b.id),
            );
          }}
          onDelete={(id) => {
            if (
              window.confirm(
                `Delete "${id}" and every model and variant under it? This cannot be undone.`,
              )
            ) {
              remove.mutate(id, {
                onSuccess: () => toast.success(`Deleted ${id}`),
                onError: (err) => toast.error(errorMessage(err)),
              });
            }
          }}
        />
      )}

      {remove.error ? <ErrorNote error={remove.error} /> : null}
    </div>
  );
}

function NewBrandForm({ onDone }: { onDone: () => void }) {
  const create = useCreateBrand();
  const toast = useToast();
  const [nameEn, setNameEn] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [brandId, setBrandId] = useState('');
  const [touchedId, setTouchedId] = useState(false);

  // The slug becomes a permanent public URL, so it is derived once from the
  // name and then left alone unless the editor changes it deliberately.
  const effectiveId = touchedId ? brandId : slugify(nameEn);

  const submit = () => {
    const body: BrandInput & { brandId: string } = {
      brandId: effectiveId,
      name: { en: nameEn.trim(), ar: nameAr.trim() },
      // Blank falls back to the English name when the feed is rendered.
      feedKey: '',
      sortOrder: 0,
      status: 'draft',
    };
    create.mutate(body, {
      onSuccess: (brand) => {
        toast.success(`Created ${brand.name.en}`);
        onDone();
      },
      onError: (err) => toast.error(errorMessage(err)),
    });
  };

  return (
    <section className="card">
      <h3>New brand</h3>
      <div className="grid-2">
        <label className="field">
          <span className="field-label">
            Name (English) <span className="required">*</span>
          </span>
          <input
            className="input"
            value={nameEn}
            autoFocus
            onChange={(e) => setNameEn(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Name (Arabic)</span>
          <input
            className="input rtl"
            dir="rtl"
            lang="ar"
            value={nameAr}
            onChange={(e) => setNameAr(e.target.value)}
          />
        </label>
      </div>
      <label className="field">
        <span className="field-label">Identifier</span>
        <input
          className="input mono"
          value={effectiveId}
          onChange={(e) => {
            setTouchedId(true);
            setBrandId(slugify(e.target.value));
          }}
        />
        <span className="field-hint">
          Permanent. Used in the published URL <code>/v1/en/brands/{effectiveId || '…'}.json</code>.
        </span>
      </label>

      {create.error ? <ErrorNote error={create.error} /> : null}

      <div className="form-actions">
        <button type="button" className="btn subtle" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="btn primary"
          data-loading={create.isPending || undefined}
          disabled={!nameEn.trim() || effectiveId.length < 2 || create.isPending}
          onClick={submit}
        >
          Create brand
        </button>
      </div>
    </section>
  );
}
